import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { feedbackService, imcService, ImcClassificacaoData } from '../services/api';

interface Feedback {
  id: number;
  titulo: string;
  mensagem: string;
  cor: string;
  icone?: string;
  ordem: number;
  enviado_email: boolean;
  data_envio_email?: string;
}

interface Usuario {
  id: string;
  nome: string;
  tipo_usuario: 'titular' | 'dependente';
  relacionamento?: string;
}

interface FeedbacksDisplayProps {
  usuarios: Usuario[];
  sessaoId: string;
  userEmail?: string;
  onEmailSent?: () => void;
}

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #f8fafc;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #1a202c;
  text-align: center;
  margin-bottom: 32px;
  font-size: 28px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const UserButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
`;

const UserButton = styled.button<{ $active: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.$active ? '#3b82f6' : '#e5e7eb'};
  background: ${props => props.$active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.$active ? '#ffffff' : '#374151'};
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CurrentUserTitle = styled.h3`
  color: #1f2937;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 24px;
`;

const FeedbackCard = styled.div<{ $cor: string }>`
  background: ${props => {
    switch (props.$cor) {
      case 'red':
        return '#fbd2d2';
      case 'yellow':
        return '#fffbeb';
      case 'green':
      default:
        return '#5eead4';
    }
  }};
  border: 3px solid ${props => {
    switch (props.$cor) {
      case 'red':
        return '#fca5a5';
      case 'yellow':
        return '#fde68a';
      case 'green':
      default:
        return '#34d399';
    }
  }};
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const IconContainer = styled.div`
  flex-shrink: 0;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    border-radius: 15px;
  }
`;

const Icon = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const FeedbackTitle = styled.h4`
  color: #1f2937;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

const FeedbackMessage = styled.p`
  color: #374151;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
`;

const EmailButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  margin: 24px auto;
  display: block;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.6);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmailButtonGrouped = styled(EmailButton)`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.4);
  margin-top: 8px;

  &:hover:not(:disabled) {
    box-shadow: 0 8px 25px rgba(14, 165, 233, 0.6);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 2px solid #fca5a5;
  color: #dc2626;
  padding: 16px;
  border-radius: 12px;
  margin: 16px 0;
  text-align: center;
  font-weight: 600;
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const PopupTitle = styled.h3`
  color: #1a202c;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px 0;
  text-align: center;
`;

const PopupMessage = styled.p`
  color: #374151;
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 24px 0;
  text-align: center;
`;

const PopupButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const FeedbacksDisplay: React.FC<FeedbacksDisplayProps> = ({
  usuarios,
  sessaoId,
  userEmail,
  onEmailSent
}) => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingGroupedEmail, setSendingGroupedEmail] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [showEmailSentPopup, setShowEmailSentPopup] = useState(false);
  const [showGroupedResultPopup, setShowGroupedResultPopup] = useState(false);
  const [groupedResult, setGroupedResult] = useState<{ email: string; total_emails: number; total_feedbacks: number } | null>(null);
  const [imcData, setImcData] = useState<ImcClassificacaoData | null>(null);
  const [imcDataUserId, setImcDataUserId] = useState<string | null>(null);

  useEffect(() => {
    if (usuarios.length > 0 && !selectedUserId) {
      setSelectedUserId(usuarios[0].id);
    }
  }, [usuarios, selectedUserId]);

  useEffect(() => {
    if (selectedUserId && sessaoId) {
      loadFeedbacks();
    }
  }, [selectedUserId, sessaoId]);

  useEffect(() => {
    if (!selectedUserId) {
      setImcData(null);
      setImcDataUserId(null);
      return;
    }
    setImcData(null);
    setImcDataUserId(null);
    const currentUserId = selectedUserId;
    let cancelled = false;
    imcService.getClassificacao(currentUserId)
      .then((data) => {
        if (!cancelled) {
          setImcData(data);
          setImcDataUserId(currentUserId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImcData(null);
          setImcDataUserId(null);
        }
      });
    return () => { cancelled = true; };
  }, [selectedUserId]);

  useEffect(() => {
    const user = usuarios.find(u => u.id === selectedUserId);
    setSelectedUser(user || null);
  }, [selectedUserId, usuarios]);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!selectedUserId || !sessaoId) {
        setError('Usuário ou sessão não encontrados');
        setLoading(false);
        return;
      }

      // Primeiro tenta gerar feedbacks
      try {
        await feedbackService.generateFeedbacks(selectedUserId, sessaoId);
      } catch (generateError) {
        // Erro ao gerar feedbacks pode ser normal se já existirem
      }
      
      // Depois carrega os feedbacks
      const data = await feedbackService.getUserFeedbacks(selectedUserId, sessaoId);
      setFeedbacks(data);
    } catch (err) {
      setError('Erro ao carregar feedbacks');
    } finally {
      setLoading(false);
    }
  };


  const generateFeedbacksForAllUsers = async () => {
    if (!usuarios || usuarios.length === 0 || !sessaoId) {
      return;
    }

    try {
      for (const usuario of usuarios) {
        try {
          await feedbackService.generateFeedbacks(usuario.id.toString(), sessaoId);
        } catch (error) {
        }
      }
    } catch (error) {
    }
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
  };

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      
      if (!userEmail) {
        setError('Email do Usuário não disponível');
        return;
      }

      if (!selectedUserId) {
        setError('Nenhum Usuário selecionado');
        return;
      }
      
      await feedbackService.sendUserEmail(selectedUserId, sessaoId, userEmail);
      
      setShowEmailSentPopup(true);
      
      if (onEmailSent) {
        onEmailSent();
      }
    } catch (err) {
      setError('Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendGroupedEmail = async () => {
    try {
      setSendingGroupedEmail(true);
      setError('');
      if (!userEmail) {
        setError('E-mail do titular não disponível');
        setSendingGroupedEmail(false);
        return;
      }
      const usuarioIds = usuarios.map(u => u.id);
      const data = await feedbackService.sendSessionEmailsGrouped(usuarioIds, userEmail);
      setGroupedResult(data);
      setShowGroupedResultPopup(true);
      if (onEmailSent) {
        onEmailSent();
      }
    } catch (err) {
      setError('Erro ao enviar e-mails agrupados');
    } finally {
      setSendingGroupedEmail(false);
    }
  };

  const parseHtmlContent = (content: string) => {
    return content
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<strong>(.*?)<\/strong>/gi, '$1')
      .replace(/<em>(.*?)<\/em>/gi, '$1')
      .replace(/<p>(.*?)<\/p>/gi, '$1\n')
      .trim();
  };

  const removeDuplicateFeedbacks = (feedbacks: Feedback[]): Feedback[] => {
    const seen = new Set<string>();
    return feedbacks.filter(feedback => {
      const key = `${feedback.titulo.trim().toLowerCase()}|${feedback.mensagem.trim().toLowerCase()}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  const uniqueFeedbacks = removeDuplicateFeedbacks(feedbacks);
  const feedbacksPositivos = uniqueFeedbacks.filter(f => f.cor === 'green');
  const feedbacksNegativos = uniqueFeedbacks.filter(f => f.cor === 'red');
  const feedbacksAmarelos = uniqueFeedbacks.filter(f => f.cor === 'yellow');

  const getImcCardProps = (classificacao: string | null | undefined) => {
    if (classificacao === undefined || classificacao === null) return null;
    const isNegativo =
      classificacao === 'Muito abaixo do peso' ||
      classificacao === 'Abaixo do peso' ||
      classificacao === 'Sobrepeso' ||
      (typeof classificacao === 'string' && classificacao.includes('Obesidade'));
    if (isNegativo) {
      return {
        cor: 'red' as const,
        titulo: 'Estado Nutricional',
        mensagem: `Você possui um IMC considerado ${classificacao}. Vale lembrar que o resultado não indica questões como o percentual de massa e gordura corporal. Busque apoio e avaliação profissional especializada para melhor direcionamento.`,
        icone: '/assets/feedback/estado_nutricional.png'
      };
    }
    return {
      cor: 'green' as const,
      titulo: 'Estado Nutricional',
      mensagem: 'Você possui um IMC considerado adequado para sua altura. Vale lembrar que o resultado não indica questões como o percentual de massa e gordura corporal. Busque apoio e avaliação profissional especializada para melhor direcionamento.',
      icone: '/assets/feedback/estado_nutricional.png'
    };
  };

  const generateConclusion = (feedbacks: Feedback[]) => {
    const hasNegative = feedbacks.some(f => f.cor === 'red');
    const hasPositive = feedbacks.some(f => f.cor === 'green');
    
    if (hasNegative && hasPositive) {
      return 'Você tem pontos positivos e áreas que podem ser melhoradas. Continue mantendo os hábitos saudáveis e busque melhorar os pontos de atenção.';
    } else if (hasNegative) {
      return 'Identificamos algumas áreas que podem ser melhoradas. Considere buscar orientação profissional para otimizar sua saúde.';
    } else if (hasPositive) {
      return 'Parabéns! Você está mantendo bons hábitos de saúde. Continue assim!';
    }
    return 'Continue cuidando da sua saúde e não hesite em procurar um profissional quando necessário.';
  };


  if (loading) {
    return (
      <Container>
        <LoadingMessage>Carregando feedbacks...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Feedbacks Personalizados</Title>
      
      {usuarios.length > 1 && (
        <UserButtonsContainer>
          {usuarios.map((usuario) => (
            <UserButton
              key={usuario.id}
              $active={selectedUserId === usuario.id}
              onClick={() => handleUserChange(usuario.id)}
            >
              {usuario.tipo_usuario === 'titular' 
                ? 'Titular' 
                : `Dependente ${usuario.relacionamento || ''}`
              }
            </UserButton>
          ))}
        </UserButtonsContainer>
      )}
      
      <div style={{ 
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        textAlign: 'center'
      }}>
        <CurrentUserTitle style={{ margin: 0 }}>
          {selectedUser?.tipo_usuario === 'titular' 
            ? 'Feedback do Titular' 
            : `Feedback do Dependente (${selectedUser?.relacionamento || 'dependente'})`
          }
        </CurrentUserTitle>
      </div>

      {imcData != null && imcDataUserId === selectedUserId && imcData.imc != null && (() => {
        const imcCard = getImcCardProps(imcData.classificacao);
        if (!imcCard) return null;
        return (
          <FeedbackCard key="estado-nutricional-imc" $cor={imcCard.cor}>
            <IconContainer>
              <Icon src={imcCard.icone} alt={imcCard.titulo} />
            </IconContainer>
            <Content>
              <FeedbackTitle>{imcCard.titulo}</FeedbackTitle>
              <FeedbackMessage>
                IMC: <strong>{imcData.imc.toFixed(1)}</strong>
                {imcData.classificacao && <> — {imcData.classificacao}</>}
                <br />
                {imcCard.mensagem}
                {imcData.erro && ` ${imcData.erro}`}
              </FeedbackMessage>
            </Content>
          </FeedbackCard>
        );
      })()}

      {uniqueFeedbacks.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#6c757d',
          fontSize: '16px'
        }}>
          Nenhum feedback disponível para este usuário.
        </div>
      ) : (
        <>
          {feedbacksPositivos.map((feedback) => (
        <FeedbackCard key={feedback.id} $cor={feedback.cor}>
          {feedback.icone && (
            <IconContainer>
              <Icon src={feedback.icone} alt={feedback.titulo} />
            </IconContainer>
          )}
          <Content>
            <FeedbackTitle>{feedback.titulo}</FeedbackTitle>
            <FeedbackMessage>
              {parseHtmlContent(feedback.mensagem)}
            </FeedbackMessage>
          </Content>
        </FeedbackCard>
      ))}

      {feedbacksAmarelos.map((feedback) => (
        <FeedbackCard key={feedback.id} $cor={feedback.cor}>
          {feedback.icone && (
            <IconContainer>
              <Icon src={feedback.icone} alt={feedback.titulo} />
            </IconContainer>
          )}
          <Content>
            <FeedbackTitle>{feedback.titulo}</FeedbackTitle>
            <FeedbackMessage>
              {parseHtmlContent(feedback.mensagem)}
            </FeedbackMessage>
          </Content>
        </FeedbackCard>
      ))}

      {feedbacksNegativos.map((feedback) => (
        <FeedbackCard key={feedback.id} $cor={feedback.cor}>
          {feedback.icone && (
            <IconContainer>
              <Icon src={feedback.icone} alt={feedback.titulo} />
            </IconContainer>
          )}
          <Content>
            <FeedbackTitle>{feedback.titulo}</FeedbackTitle>
            <FeedbackMessage>
              {parseHtmlContent(feedback.mensagem)}
            </FeedbackMessage>
          </Content>
        </FeedbackCard>
      ))}

      {uniqueFeedbacks.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
          border: '3px solid #fb923c',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '24px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <h4 style={{
            fontSize: '20px',
            fontWeight: '700',
            marginBottom: '16px',
            color: '#1f2937'
          }}>
            Conclusão
          </h4>
          <p style={{
            margin: 0,
            fontSize: '16px',
            lineHeight: '1.6',
            color: '#374151'
          }}>
            {generateConclusion(uniqueFeedbacks)}
          </p>
        </div>
      )}
        </>
      )}



      {userEmail && (
        <EmailButton 
          onClick={handleSendEmail} 
          disabled={sendingEmail}
        >
          {sendingEmail ? 'Enviando...' : 'Enviar feedback do usuário por e-mail'}
        </EmailButton>
      )}

      <EmailButtonGrouped
        onClick={handleSendGroupedEmail}
        disabled={sendingGroupedEmail || !userEmail}
      >
        {sendingGroupedEmail ? 'Enviando...' : 'Enviar feedbacks de todos usuários por e-mail'}
      </EmailButtonGrouped>

      {showEmailSentPopup && (
        <PopupOverlay onClick={() => setShowEmailSentPopup(false)}>
          <PopupContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <PopupTitle>E-mail Enviado!</PopupTitle>
            <PopupMessage>
              O e-mail com os feedbacks foi enviado com sucesso para {userEmail}.
            </PopupMessage>
            <PopupButton onClick={() => setShowEmailSentPopup(false)}>
              OK
            </PopupButton>
          </PopupContainer>
        </PopupOverlay>
      )}

      {showGroupedResultPopup && groupedResult && (
        <PopupOverlay onClick={() => setShowGroupedResultPopup(false)}>
          <PopupContainer onClick={(e: React.MouseEvent) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <PopupTitle>E-mail enviado</PopupTitle>
            <PopupMessage>
              E-mail(s) enviado(s) para {groupedResult.email}.
            </PopupMessage>
            <PopupButton onClick={() => setShowGroupedResultPopup(false)}>
              OK
            </PopupButton>
          </PopupContainer>
        </PopupOverlay>
      )}
    </Container>
  );
};

export default FeedbacksDisplay;